import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDealership } from '../../../../store/zustand/dealershipStore'
import { createHtmlPortalNode, OutPortal } from 'react-reverse-portal'
import { useGetTagCodes } from '../../../../api/utils/useGetTagCodes'
import { Link, Outlet, useNavigate } from 'react-router-dom'
import { createColumnHelper } from '@tanstack/react-table'
import { VendorAudience } from '../../../../lib/types/Audience'
import dayjs from 'dayjs'
import { GA4 } from '../../../../lib/utils/ga4'
import mtm from '../../../../lib/utils/mtm'
import {
  Button,
  Info,
  LoadingComponent,
  Pill,
  SelectInput,
  Table,
} from '@demandlocal/components'
import { formatNumber } from '../../../../lib/utils/formatNumber'
import { VENDOR } from '../../../../lib/utils/vendors'
import { motion } from 'framer-motion'
import FiltersContext from '../../../../components/Filters/FiltersContext'
import { useGetThirdPartyAudiences } from '../../../../api/dealership/customers/audiences/useGetThirdPartyAudiences'
import { ReactComponent as GoogleIcon } from './../../../../images/icons/audience_google.svg'
import { ReactComponent as MailchimpIcon } from './../../../../images/icons/audience_mailchimp.svg'
import { ReactComponent as FacebookIcon } from './../../../../images/icons/audience_facebook.svg'
import { ReactComponent as TradeDeskIcon } from './../../../../images/icons/audience_tradedesk.svg'
import { ReactComponent as AmazonIcon } from './../../../../images/icons/audience_amazon.svg'
import { useGetOverallStats } from '../../../../api/dealership/useGetOverallStats'
import { ReactComponent as AddCustomersImage } from '../../../../images/add_customers.svg'
import { BuildAudienceGuide } from '../../../../components/Audiences/BuildAudienceGuide'

const thirdPartyAudienceColumnHelper = createColumnHelper<VendorAudience>()

export default function PlatformAudiences(): JSX.Element {
  const dealership = useDealership()
  const { t } = useTranslation('routes')
  const navigate = useNavigate()
  const filtersPortalNode = useMemo(() => createHtmlPortalNode(), [])
  const [filters, setFilters] = useState<Record<string, string>>({})
  const [filteredTotal, setFilteredTotal] = useState<number>(0)

  const { data: tag_codes } = useGetTagCodes({ pageSize: 100 })

  const { data: thirdPartyAudiences, isLoading: isLoadingThirdPartyAudiences } =
    useGetThirdPartyAudiences({ pageSize: 1 })

  const hasAudiences = useMemo(
    () =>
      !isLoadingThirdPartyAudiences &&
      thirdPartyAudiences?.pagination?.totalDocs &&
      thirdPartyAudiences?.pagination?.totalDocs > 0,
    [isLoadingThirdPartyAudiences, thirdPartyAudiences?.pagination.totalDocs],
  )

  const thirdPartyAudienceColumns = useMemo(
    () => [
      thirdPartyAudienceColumnHelper.accessor('vendor_audience_name', {
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to={t('dealership.audiences.report', {
              dealership,
              audience: {
                platform_id: row.original.vendor_id,
                id: row.original._id,
              },
              start_date: dayjs()
                .subtract(1, 'month')
                .startOf('month')
                .format('YYYY-MM-DD'),
              end_date: dayjs().format('YYYY-MM-DD'),
            })}
            className={'text-primary-persian-blue font-bold'}
            onClick={() => {
              GA4.sendEvent({
                event_name: 'view_audience_details',
                event_params: { audience_id: row.original.audience_id },
              })
              mtm.sendEvent('View', 'Audience', 'Report', row.original.audience_id)
            }}>
            {row.original.vendor_audience_name}
          </Link>
        ),
        enableSorting: false,
      }),
      thirdPartyAudienceColumnHelper.accessor('vendor_audience_details.isAutoGenerated', {
        header: 'Type',
        cell: (info) =>
          info.getValue() ? (
            <Pill type={'success'}>AI Generated</Pill>
          ) : info.row.original.vendor_audience_name.includes('[LinkOne]') ||
            info.row?.original?.vendor_audience_details?.audience_type === 'USER_LIST' ? (
            <Pill type={'info-purple'}>1st Party</Pill>
          ) : (
            <Pill type={'warning'}>3rd Party</Pill>
          ),
        enableSorting: false,
      }),
      thirdPartyAudienceColumnHelper.accessor('vendor_audience_details.personsCount', {
        header: 'People',
        cell: (info) => {
          const value =
            info.getValue() ||
            info.row?.original?.vendor_audience_details?.estimatedSize?.max ||
            0
          return value ? formatNumber(value) : 'N/A'
        },
        enableSorting: false,
      }),
      thirdPartyAudienceColumnHelper.accessor('vendor_audience_details.householdCount', {
        header: 'Households',
        cell: (info) => {
          const value =
            info.getValue() ||
            info.row?.original?.vendor_audience_details?.estimatedSize?.min ||
            0
          return value ? formatNumber(value) : 'N/A'
        },
        enableSorting: false,
      }),
      thirdPartyAudienceColumnHelper.accessor('metrics.impressions', {
        header: '30d Impressions',
        cell: (info) => {
          return formatNumber(info.getValue() || 0)
        },
        enableSorting: false,
      }),
      thirdPartyAudienceColumnHelper.accessor('metrics.clicks', {
        header: '30d Clicks',
        cell: (info) => {
          return formatNumber(info.getValue() || 0)
        },
        enableSorting: false,
      }),
      thirdPartyAudienceColumnHelper.accessor('metrics.campaigns', {
        header: 'Campaigns',
        cell: (info) => {
          return formatNumber(info.getValue() || 0)
        },
        enableSorting: false,
      }),
      thirdPartyAudienceColumnHelper.display({
        header: () => {
          return (
            <div className="flex flex-row items-center gap-2">
              <span>Media Conversion Rate</span>
              <Info message="(Last 30d) Percentage of users that engage with an ad and complete a key site action, which is calculated by dividing the total number of conversions by the number of impressions" />
            </div>
          )
        },
        id: 'conversion_rate',
        cell: (info) => {
          if (info.row.original.vendor_id === VENDOR.TradeDesk) {
            const conversions = tag_codes?.data
              ?.filter((tag) => tag.id !== 4)
              ?.reduce(
                (acc, cur) => acc + (info.row?.original?.metrics?.[cur.tag_name] ?? 0),
                0,
              )

            return `${
              Math.round(
                !Boolean(info.row.original?.metrics?.impressions)
                  ? 0
                  : (10000 * (conversions ?? 0)) /
                      (info.row.original?.metrics?.impressions ?? Infinity),
              ) / 100
            }%`
          } else if (
            info.row.original.vendor_id === VENDOR.AmazonCognition ||
            info.row.original.vendor_id === VENDOR.Google
          ) {
            return info.row.original.metrics?.impressions
              ? `${
                  Math.round(
                    ((info.row.original.metrics?.total_conversions_1 || 0) * 10000) /
                      info.row.original.metrics?.impressions,
                  ) / 100
                }%`
              : '0%'
          }
        },
      }),
      thirdPartyAudienceColumnHelper.accessor(
        (row) => row.vendor_audience_details.lastUpdatedAtUTC,
        {
          id: 'vendor_audience_details.lastUpdatedAtUTC',
          header: 'Last update',
          cell: (info) =>
            info.getValue() ? dayjs(info.getValue()).format('MMM DD, YYYY') : '-',
          enableSorting: true,
        },
      ),
      thirdPartyAudienceColumnHelper.display({
        id: 'source',
        header: () => 'Marketed on',
        cell: ({ row }) => (
          <>
            {row.original.vendor_id === VENDOR.Google && <GoogleIcon />}
            {row.original.vendor_id === VENDOR.Mailchimp && <MailchimpIcon />}
            {row.original.vendor_id === VENDOR.Facebook && <FacebookIcon />}
            {row.original.vendor_id === VENDOR.TradeDesk && <TradeDeskIcon />}
            {row.original.vendor_id === VENDOR.AmazonCognition && <AmazonIcon />}
          </>
        ),
      }),
    ],
    [dealership, t, tag_codes?.data],
  )

  const platformOptions = useMemo(() => {
    return Object.entries(VENDOR)
      .filter(([key, value]) => typeof value === 'number')
      .map(([key, value]) => ({
        label: key === 'AmazonCognition' ? 'Amazon' : key,
        value,
      }))
      .filter(({ value }) =>
        [
          VENDOR.TradeDesk,
          VENDOR.AmazonCognition,
          VENDOR.Google,
          VENDOR.Facebook,
        ].includes(Number(value)),
      )
  }, [])

  const { data: { totalCustomers = 0 } = {}, isLoading: isLoadingOverallStats } =
    useGetOverallStats()

  const hasNoAudiences = useMemo(
    () =>
      !isLoadingThirdPartyAudiences &&
      Number(thirdPartyAudiences?.pagination.totalDocs) === 0,
    [isLoadingThirdPartyAudiences, thirdPartyAudiences?.pagination.totalDocs],
  )
  const hasNoCustomers = useMemo(() => totalCustomers === 0, [totalCustomers])

  if (isLoadingOverallStats) return <LoadingComponent title={'Loading Audiences'} />

  if (hasNoAudiences && hasNoCustomers) {
    return (
      <div className="flex flex-col h-full justify-center items-center">
        <div
          className={'flex flex-col gap-5 my-auto mx-auto items-center justify-center'}>
          <AddCustomersImage className="mx-auto" />
          <span className={'font-bold text-3xl font-Spartan'}>
            You have no customers yet
          </span>
          <span className={'text-base font-Spartan'}>
            You must add customers before creating an audience
          </span>
          <div className={'w-2/3'}>
            <BuildAudienceGuide />
          </div>
          <Button
            variant="rainbow"
            onClick={() =>
              navigate(t('dealership.customers.sources.create', { dealership }))
            }>
            + Add customers
          </Button>
          <Outlet />
        </div>
      </div>
    )
  }

  return (
    <motion.div className={'flex flex-1 flex-col p-10 font-Spartan'}>
      <div className={'w-full flex flex-1 flex-col gap-2'}>
        <span className="text-md py-3">
          Purchased or created audiences tailored for programmatic campaigns managed by
          LinkOne. With 1000's of options and companies selling audiences we can show
          insights to all audiences running on programatic ads. (Meta and Google Audiences
          do not show)
        </span>
        <div className={'pb-4 flex justify-between'}>
          <div>
            <div
              className={
                'font-Spartan font-bold text-white p-2 rounded-md text-[10px] bg-primary-xiketic'
              }>
              {formatNumber(filteredTotal)} audiences
            </div>
          </div>
          <div>
            <OutPortal node={filtersPortalNode} />
          </div>
        </div>
        <FiltersContext portalNode={filtersPortalNode} setFilters={setFilters}>
          <SelectInput
            name={'type'}
            label={'Type'}
            isClearable
            options={[
              {
                label: '1st Party',
                value: 'first_party',
              },
              {
                label: '3rd Party',
                value: 'third_party',
              },
              {
                label: 'AI Generated',
                value: 'ai_generated',
              },
            ]}
          />
          <SelectInput
            name={'vendor_id'}
            label={'Marketed on'}
            options={platformOptions}
            isClearable
            isMulti
          />
        </FiltersContext>
        <Table
          columns={thirdPartyAudienceColumns}
          filters={filters}
          fetchFn={useGetThirdPartyAudiences}
          defaultSorting={[
            { id: 'vendor_audience_details.lastUpdatedAtUTC', desc: true },
          ]}
          setTotalRows={(v) => setFilteredTotal(v)}
        />
      </div>
      <Outlet />
    </motion.div>
  )
}
